import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Input, Layout, List, Card, Tag, Tooltip } from 'antd';
import { PageHeader } from '@ant-design/pro-layout';
import { buildPageHeaderBreadcrumb } from '../../updated/utils'; 
import { CompanyContext } from '../../providers/companyProvider'; 
import { authService } from '../../auth/authService';
import { Routes } from '../../routes'; 
import { flags, DEFAULT_COMPANY_VARIABLES } from '../../featureFlags/flags';
import './styles.less';

const VarsPage = () => {

    /********************
     * ANCHOR Vars
     ********************/

    const { company } = useContext( CompanyContext );
    const [ companyId ] = useState( authService.getClaims()?.[ 'company_id' ] );
    const [ companyList, setCompanyList ] = useState();
    const [ companyName, setCompanyName ] = useState();
    const [ companyLogo, setCompanyLogo ] = useState();
    const [ filterValue, setFilterValue ] = useState();
     
    /***************************
     * ANCHOR Load Company Data 
     ***************************/

    useEffect( () => {
        if ( companyList && companyId ) {
            const company = companyList.find( i => i.id === companyId );
            setCompanyName( company?.name );
            setCompanyLogo( company?.primaryLogo );
        }
    }, [ companyList, companyId ] );

    const companyData = useMemo( () => {
        if ( companyId && companyName ) {
            const result = [];

            result.push( {
                name: companyName,
                value: companyId,
                isCompanyName: true
            } );
        
            return result;
        } else {
            return [];
        }
    }, [ companyId, companyName ] );

    /****************************
     * ANCHOR Load Company Vars
     ****************************/

    const companyVars = useMemo( () => {
        // Get company variables and check if they are broken
        const result = company.variableList.map( i => {
            return { 
                ...i,
                broken: i.value.toString() !== i.value.toString().trim()
            };
        } );

        // Add default varibles to result if they are not already there
        for ( let [ , value ] of Object.entries( flags ) ) {
            const flagExists = company.variableList.find( v => v.name == value );
 
            if ( flagExists === undefined ) {
                result.push( {
                    name: value,
                    value: (
                        <span>
                            <Tag color="green">Default</Tag>
                            { `${ ( DEFAULT_COMPANY_VARIABLES[ value ] ?? 'false' ).toString() }` }
                        </span>
                    )
                } );
            }    
        }

        // Sort alphabetically
        return result.sort( ( a, b ) => {
            if ( a.name < b.name ) {
                return -1;
            }
            
            if ( a.name > b.name ) {
                return 1;
            }
            return 0;
        } );
    }, [] );

    /************************
     * ANCHOR Event Handlers
     ***********************/

    const handleFilterInputChange = ( e ) => {
        setFilterValue( e.target.value.toLowerCase() );
    };

    /**************************
     * ANCHOR Utility functons
     **************************/

    // Filters companyVars by the filterValue
    const filteredCompanyVars = useMemo( () => {
        return filterValue && companyVars.filter( i => i.name.toLowerCase().includes( filterValue ) );
    }, [ filterValue, companyVars ] );

    // Separate camel cased string into spaced string
    const splitIntoSpacedString = ( str ) => {
        return str.replace( /[A-Z]/g, ' $&' );
    };

    /***************************
     * ANCHOR  Render Functions 
     **************************/

    const renderCompany = () => {
        return ( 
            <List
                size="small"
                itemLayout="horizontal"
                dataSource={ companyData }
                renderItem={ ( item ) => {
                    if ( item.name === 'users' ) {
                        return (
                            <List.Item>
                                <List.Item.Meta
                                    title="Users with engagement" />
                                <div>{ item.value }</div>
                            </List.Item>
                        );
                    }

                    return (
                        <List.Item>
                            <List.Item.Meta
                                title={ splitIntoSpacedString( item.name ) } />
                            <div>{ item.value }</div>
                        </List.Item>
                    );
                } } 
            />
        );
    };
 
    const renderCompanyVariableListItem = ( item ) => {
        return (
            <List.Item>
                <List.Item.Meta
                    title={ item.name } 
                />
                { item.broken && 
                    <Tooltip title="This value contains leading or trailing spaces and may not work properly">
                        <span className="broken">{ item.value }</span>
                    </Tooltip>
                }

                { !item.broken &&
                    <div className={ item.broken ? 'broken' : '' }>{ item.value }</div>
                }
            </List.Item> 
        );
    };

    /*********************
     * ANCHOR Render
     *********************/

    return (
        <div className="stats-page">
            <Layout>
                <PageHeader     
                    breadcrumb={ buildPageHeaderBreadcrumb( [
                        { breadcrumbName: 'Course Studio', path: Routes.Home.buildPath() }, 
                        { breadcrumbName: 'Variables' }
                    ] ) } 
                />
                
                <Card 
                    title="Company"
                >
                    { renderCompany() }
                </Card>
                    
                <Card
                    title="Company Variables"
                    extra={ 
                        <Input.Search
                            allowClear
                            placeholder='Filter'
                            onChange={ handleFilterInputChange } />
                    }
                >
                    <List
                        size="small"
                        itemLayout="horizontal"
                        dataSource={ filteredCompanyVars || companyVars }
                        renderItem={ ( item ) => renderCompanyVariableListItem( item ) } 
                    />
                </Card>
            </Layout>
        </div>
    );
};

export default VarsPage;
